<template>
  <div class="donut-chart">
    <div class="p-3 pb-0">
      <h6 class="mt-3 ms-4">{{ title }}</h6>
      <!-- eslint-disable vue/no-v-html -->
      <p v-if="description" class="text-sm ms-4" v-html="description" />
    </div>
    <div class="p-3">
      <div
        v-if="isLoading"
        class="chart chart-skeleton"
        :style="{ height: chartHeight }"
        aria-hidden="true"
      >
        <div class="skeleton skeleton-circle"></div>
        <div class="skeleton skeleton-line skeleton-line-lg"></div>
        <div class="skeleton skeleton-line skeleton-line-md"></div>
      </div>
      <div v-else-if="hasChartData" class="chart" :style="{ height: chartHeight }">
        <canvas
          :id="id"
          :key="chartRenderKey"
          ref="canvas"
          class="chart-canvas"
        ></canvas>
        <!-- total admite HTML de confianza y saltos de línea con \n. -->
        <div
          v-show="chartTotal"
          ref="centerText"
          class="chart-center-text"
          v-html="chartTotal"
        ></div>
      </div>
      <div
        v-else
        class="chart chart-empty"
        :style="{ height: chartHeight }"
      >
        <span class="text-sm text-muted">Sin datos</span>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js/auto";

export default {
  name: "DonutChart",
  props: {
    id: {
      type: String,
      required: true,
    },
    height: {
      type: [String, Number],
      default: "300",
    },
    title: {
      type: String,
      default: "",
    },
    description: {
      type: String,
      default: "",
    },
    total: {
      type: String,
      default: "",
    },
    chart: {
      type: Object,
      required: true,
      datasets: {
        data: Array,
        label: String
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
    chartColors: {
      type: Array,
      default: () => [],
    },
    showLegend: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      renderTimeout: null,
      renderAttempts: 0,
    };
  },
  computed: {
    chartHeight() {
      if (typeof this.height === "number") {
        return `${this.height}px`;
      }

      return /^\d+$/.test(this.height) ? `${this.height}px` : this.height;
    },
    chartDatasets() {
      if (Array.isArray(this.chart.datasets)) {
        return this.chart.datasets;
      }

      if (Array.isArray(this.chart.datasets?.data)) {
        return this.chart.datasets.data;
      }

      return Object.values(this.chart.datasets || {}).filter((dataset) => {
        return dataset && typeof dataset === "object" && "valor" in dataset;
      });
    },
    chartLabels() {
      return this.chartDatasets.map((dataset) => dataset.label);
    },
    chartValues() {
      return this.chartDatasets.map((dataset) => {
        return this.normalizeChartValue(dataset.valor);
      });
    },
    chartTotal() { 
      return this.total ?? "";
    },
    resolvedChartColors() {
      const defaultColors = [
        this.getCssVariable("--bs-analiza-dark", "#202945"),
        this.getCssVariable("--bs-analiza-azul", "#236092"),
        this.getCssVariable("--bs-analiza-medium", "#6fb1c8"),
      ];

      return this.chartColors.length ? this.chartColors : defaultColors;
    },
    hasChartData() {
      return this.chartValues.some((value) => value !== null && value > 0);
    },
    isLoading() {
      return this.loading;
    },
    chartRenderKey() {
      return `${this.id}-${this.chartDatasets.length}-${this.chartValues.join("-")}-${this.resolvedChartColors.join("-")}-${this.showLegend}`;
    },
  },
  watch: {
    loading() {
      this.$nextTick(() => {
        this.queueRenderChart();
      });
    },
    chart: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.queueRenderChart();
        });
      },
    },
    chartColors: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.queueRenderChart();
        });
      },
    },
    showLegend() {
      this.$nextTick(() => {
        this.queueRenderChart();
      });
    },
  },
  mounted() {
    this.queueRenderChart();
  },
  beforeUnmount() {
    if (this.renderTimeout) {
      clearTimeout(this.renderTimeout);
    }

    this.destroyChart();
  },
  methods: {
    destroyChart() {
      let chartStatus = Chart.getChart(this.id);
      if (chartStatus != undefined) {
        chartStatus.destroy();
      }
    },
    normalizeChartValue(value) {
      if (typeof value === "number") {
        return value;
      }

      if (value === null || value === undefined || value === "") {
        return null;
      }

      const normalizedValue = String(value)
        .replace(/\s/g, "")
        .replace("€", "")
        .replace(/\./g, "")
        .replace(",", ".");
      const numericValue = Number(normalizedValue);

      return Number.isNaN(numericValue) ? null : numericValue;
    },
    getCssVariable(name, fallback) {
      return getComputedStyle(document.documentElement)
        .getPropertyValue(name)
        .trim() || fallback;
    },
    hexToRgba(hex, alpha) {
      if (!hex || !hex.startsWith("#")) {
        return hex;
      }

      const normalizedHex = hex.replace("#", "");
      const bigint = parseInt(normalizedHex, 16);
      const red = (bigint >> 16) & 255;
      const green = (bigint >> 8) & 255;
      const blue = bigint & 255;

      return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
    },
    queueRenderChart() {
      if (this.renderTimeout) {
        clearTimeout(this.renderTimeout);
      }

      this.renderTimeout = setTimeout(() => {
        this.renderChart();
      }, 0);
    },
    renderChart() {
      if (this.isLoading || !this.hasChartData) {
        this.destroyChart();
        return;
      }

      const canvas = this.$refs.canvas;

      if (!canvas) {
        return;
      }

      if (!canvas.clientWidth && this.renderAttempts < 5) {
        this.renderAttempts += 1;
        this.renderTimeout = setTimeout(() => {
          this.renderChart();
        }, 50);
        return;
      }

      this.renderAttempts = 0;

      const ctx = canvas.getContext("2d");
      const chartColors = this.resolvedChartColors;

      this.destroyChart();

      const centerTextPlugin = {
        id: `${this.id}-center-text`,
        afterDraw: (chart) => {
          const centerText = this.$refs.centerText;
          const firstArc = chart.getDatasetMeta(0).data[0];

          if (!centerText || !firstArc) {
            return;
          }

          // Use the arc center so the text stays aligned when the legend resizes.
          Object.assign(centerText.style, {
            left: `${firstArc.x}px`,
            top: `${firstArc.y}px`,
            width: `${Math.max(0, firstArc.innerRadius * 2 - 12)}px`,
            visibility: "visible",
          });
        },
      };

      new Chart(ctx, {
        type: "doughnut",
        data: {
          labels: this.chartLabels,
          datasets: [
            {
              data: this.chartValues,
              backgroundColor: this.chartValues.map((value, index) => {
                return this.hexToRgba(chartColors[index % chartColors.length], 0.9);
              }),
              borderColor: "#fff",
              borderWidth: 2,
              hoverOffset: 6,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "68%",
          plugins: {
            legend: {
              display: this.showLegend,
              position: "bottom",
              align: "center",
              labels: {
                boxWidth: 12,
                boxHeight: 12,
                color: "#333",
                padding: 18,
                usePointStyle: true,
                pointStyle: "circle",
                font: {
                  size: 13,
                  family: "Nunito Sans",
                  style: "normal",
                },
              },
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const label = context.label || "";
                  const value = context.parsed;

                  return `${label}: ${value}`;
                },
              },
            },
          },
        },
        plugins: [centerTextPlugin],
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.donut-chart {
  width: 100%;
  background-color: var(--bs-gray-200);
}

.chart {
  position: relative;
  width: 100%;
  min-height: 200px;
}

.chart-empty,
.chart-skeleton {
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-skeleton {
  flex-direction: column;
  gap: 1.25rem;
}

.chart-canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
}

.chart-center-text {
  position: absolute;
  transform: translate(-50%, -50%);
  visibility: hidden;
  pointer-events: none;
  color: #202945;
  font: 500 14px "Nunito Sans", sans-serif;
  text-align: center;
  white-space: pre-line;
  overflow-wrap: anywhere;
}

.skeleton {
  position: relative;
  overflow: hidden;
  background: #e9ecef;
  border-radius: 0.35rem;

  &::after {
    position: absolute;
    inset: 0;
    content: "";
    transform: translateX(-100%);
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.75) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    animation: skeleton-shimmer 1.25s infinite;
  }
}

.skeleton-circle {
  width: 8rem;
  height: 8rem;
  border-radius: 50%;
}

.skeleton-line {
  height: 1rem;
}

.skeleton-line-lg {
  width: 68%;
}

.skeleton-line-md {
  width: 48%;
}

@keyframes skeleton-shimmer {
  100% {
    transform: translateX(100%);
  }
}
</style>
